import Immutable from 'immutable';
import React from 'react';
import BaseToolbar from 'components/Toolbar/BaseToolbar';
import { HOVER_HINT } from 'components/Toolbar/constants';
import AccessibilityRoleControl, { AccessibilityOption } from 'components/Toolbar/Controls/AccessibilityRole';
import BackgroundImage from 'components/Toolbar/Controls/BackgroundImage';
import Border from 'components/Toolbar/Controls/Border';
import BorderRadius from 'components/Toolbar/Controls/BorderRadius';
import ColumnWidth from 'components/Toolbar/Controls/ColumnWidth';
import FillColor from 'components/Toolbar/Controls/FillColor';
import Padding from 'components/Toolbar/Controls/Padding';
import Responsive from 'components/Toolbar/Controls/Responsive';
import Scrollable from 'components/Toolbar/Controls/Scrollable';
import ControlGroup from 'components/Toolbar/Shared/ControlGroup';
import { AccessibilityRole, ProjectsConfig, ProjectType } from 'const';
import { LayoutStylesState, LayoutStylesStateSetters } from 'hooks/layout/useLayoutStyles';
import { intlGet } from 'utils/intlGet';

const LAYOUT_ACCESSIBILITY_OPTIONS: AccessibilityOption[] = [
  {
    value: AccessibilityRole.PRESENTATION,
    text: intlGet('AccessibilityRole.ArtboardLayout', AccessibilityRole.PRESENTATION),
  },
  {
    value: AccessibilityRole.NORMAL,
    text: intlGet('AccessibilityRole.ArtboardLayout', AccessibilityRole.NORMAL),
  },
];

type Props = {
  styles: LayoutStylesState;
  stylesSetters: LayoutStylesStateSetters;
  addLayoutColumn(): void;
  applyColumnsWidth?(columnsWidth: Immutable.List<number>): void;
  columnsWidth: Immutable.List<number>;
  deleteLayoutColumn?(position: number): void;
  getMinColumnWidth?(position: number): number;
  isAddingDisabled?: boolean;
  layoutId: string;
  projectType: ProjectType;
  currentAccessibilityRole: AccessibilityRole;
  changeAccessibilityRole(value: AccessibilityRole): void;
};

const Toolbar: React.FunctionComponent<Props> = (props) => {
  const {
    styles,
    stylesSetters,
    addLayoutColumn,
    applyColumnsWidth,
    columnsWidth,
    deleteLayoutColumn,
    getMinColumnWidth,
    isAddingDisabled,
    layoutId,
    projectType,
    changeAccessibilityRole,
    currentAccessibilityRole,
  } = props;
  const { allowBackgroundOpacity, allowBackgroundImage, allowScrollableLayout } = ProjectsConfig[projectType];

  return (
    <BaseToolbar title={intlGet('EditorToolbar.Asset', 'Layout')} layoutId={layoutId} dataTestId='artboardLayout-baseToolbar'>
      <ControlGroup dataTestId='controlGroup0'>
        <ColumnWidth
          addLayoutColumn={addLayoutColumn}
          applyColumnsWidth={applyColumnsWidth}
          columnsWidth={columnsWidth}
          deleteLayoutColumn={deleteLayoutColumn}
          getMinColumnWidth={getMinColumnWidth}
          isAddingDisabled={isAddingDisabled}
          dataTestId='artboardLayout-columnWidth'
        />
        <Padding
          padding={styles.padding}
          togglePadding={stylesSetters.padding}
          dataTestId='artboardLayout-padding'
        />
        <Border
          border={styles.border}
          toggleBorder={stylesSetters.border}
          dataTestId='artboardLayout-border'
        />
        <FillColor
          activeColor={styles.backgroundColor}
          toggleColor={stylesSetters.backgroundColor}
          opacity={styles.backgroundColorOpacity}
          toggleOpacity={allowBackgroundOpacity ? stylesSetters.backgroundColorOpacity : null}
          gradient={styles.backgroundGradient}
          toggleGradient={stylesSetters.backgroundGradient}
          dataTestId='artboardLayout-fillColor'
        />
        {allowBackgroundImage && <BackgroundImage
          backgroundImage={styles.backgroundImage}
          toggleBackgroundImage={stylesSetters.backgroundImage}
          dataTestId='artboardLayout-backgroundImage'
        />}
        <BorderRadius
          borderRadius={styles.borderRadius}
          title={HOVER_HINT.LAYOUT}
          dropdownWarningMessage={intlGet('EditorToolbar.Tooltip', 'WarningMessage')}
          toggleBorderRadius={stylesSetters.borderRadius}
          dataTestId='artboardLayout-borderRadius'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup1'>
        <Responsive
          responsive={styles.responsive}
          toggleResponsive={(): void => stylesSetters.responsive(!styles.responsive)}
          dataTestId='artboardLayout-responsive'
        />
        {allowScrollableLayout && <Scrollable
          scrollable={styles.scrollable}
          toggleScrollable={(): void => stylesSetters.scrollable(!styles.scrollable)}
          dataTestId='artboardLayout-scrollable'
        />}
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup2'>
        <AccessibilityRoleControl
          accessibilityOptions={LAYOUT_ACCESSIBILITY_OPTIONS}
          currentAccessibilityRole={currentAccessibilityRole}
          changeAccessibilityRole={changeAccessibilityRole}
          dataTestId='artboardLayout-accessibilityRole'/>
      </ControlGroup>
    </BaseToolbar>
  );
};

export default Toolbar;
